import React, { useEffect, useRef, useState } from "react";
import {
  getLeaderBoard,
  getLeaderboardData,
  followUnfollow,
  getOrder,
  getUserPerformance,
} from "../../api/user";
import PageContainer from "../pages/PageContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import LeaderboardSkeleton from "../Skeleton/LeaderboardSkeleton";
import useDelayedFlag from "../Skeleton/useDelayedFlag";
import { SectionError } from "../Skeleton/LoadError";
import GreenTick from "../../images/green-tick.png";
import GameSideBar from "../Games/GameSideBar";
import useLeagueContext from "../../utils/useLeagueContext";
import { standaloneLeagueProfilePath } from "../../utils/leaguePath";
import { getLeagueStatus } from "../../pages/Game/landing/leagueStatus";
import { leagueTypeKey } from "../../pages/Game/landing/leagueFormat";
import Flag from "react-world-flags";
import { COUNTRIES } from "../../icons/countryFlag";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "./PullToRefreshIndicator";

const TABS = [
  { key: "lifetime", label: "Lifetime" },
  { key: "monthly", label: "Monthly" },
  { key: "weekly", label: "Weekly" },
  { key: "daily", label: "Daily" },
];

const MEDAL_COLORS = {
  1: "#D4A017",
  2: "#C0C0C0",
  3: "#CD7F32",
};

const renderFlag = (code) => {
  const match = COUNTRIES.find((item) => item.iso2 === code);
  if (match) {
    return (
      <img
        key={match.iso2}
        src={match.flag}
        alt={match.name}
        width="20"
        height="14"
        className="inline ml-1.5 align-middle"
      />
    );
  }
  if (code) {
    return (
      <Flag
        code={code}
        style={{ width: 20, height: "auto" }}
        className="inline ml-1.5 align-middle"
      />
    );
  }
  return null;
};

// Cash Games only. Client: "Put the same green check beside qualified players on
// the podium and leaderboard rows. Create a spot/gutter ... after the username or
// flag (if they have one)." `prize_eligibility` is only sent for a Cash Game
// (LeaderBoardController), so every other board renders nothing here.
const PrizeEligibleCheck = ({ user }) =>
  user?.prize_eligibility?.eligible ? (
    <span
      role="img"
      aria-label="Prize eligible"
      className="flex-shrink-0 ml-1 font-bold text-greenXD"
    >
      ✓
    </span>
  ) : null;

// The viewer's own progress line on the "You're ranked" card. The client gave a
// short phone copy and a longer desktop copy for the same fact.
const PrizeProgressLine = ({ eligibility }) => {
  if (!eligibility) return null;
  if (eligibility.eligible) {
    return (
      <p className="text-sm font-semibold text-greenXD">✓ Prize eligible</p>
    );
  }
  const days = Math.min(Number(eligibility.trading_days) || 0, 2);
  const closed = Math.min(Number(eligibility.closed_positions) || 0, 1);
  return (
    <p className="text-sm text-gray-500">
      <span className="sm:hidden">
        Prize: {days}/2 days · {closed}/1 closed
      </span>
      <span className="hidden sm:inline">
        Prize eligibility: {days} of 2 trading days · {closed} of 1 positions closed
      </span>
    </p>
  );
};

// Return % formatting — leaderboard is the only surface that keeps the `+`.
// 2 decimals everywhere; positive green w/ `+`, negative red (carries its own
// `-`), exactly zero grey with NO sign.
const formatPerformance = (user) => {
  const raw = user?.performance ?? user?.avg_performance ?? 0;
  const num = Number(raw);
  const text =
    num > 0
      ? `+${num.toFixed(2)}%`
      : num < 0
      ? `${num.toFixed(2)}%`
      : "0.00%";
  const colorClass =
    num > 0 ? "text-greenXD" : num < 0 ? "text-red-600" : "text-gray-500";
  return { value: num, text, colorClass };
};

const formatFollowers = (n) => {
  const num = Number(n ?? 0);
  if (num >= 10000) return `${Math.round(num / 1000)}K`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

// "1 Fan" vs "N Fans".
const fansLabel = (n) => (Number(n) === 1 ? "Fan" : "Fans");

// Mirror of NewPortfolioSection.calculateGroupPnL — same buy/cover vs sell/short rule.
const calculateGroupStatus = (trades) => {
  let buyCoverTotal = 0;
  let sellShortTotal = 0;
  trades.forEach((trade) => {
    const tradeValue =
      parseFloat(trade.order_quantity) * parseFloat(trade.order_price);
    if (trade.order_type === 1 || trade.order_type === 5) {
      buyCoverTotal += tradeValue;
    } else if (trade.order_type === 2 || trade.order_type === 3) {
      sellShortTotal += tradeValue;
    }
  });
  if (buyCoverTotal > 0 && sellShortTotal > 0) {
    if (buyCoverTotal < sellShortTotal) return "profit";
    if (buyCoverTotal > sellShortTotal) return "loss";
  }
  return "neutral";
};

// Returns true Max Drawdown as a signed percentage (≤ 0), or null when not
// computable. Drawdown is measured on EQUITY (equity index = 1 + return), not on
// the raw return percentage — equity can't go below zero, so the worst possible
// loss is 100% of the peak (dividing raw return %s by the peak return could print
// impossible values like 142.9%). Walks rows chronologically tracking the running
// peak equity, so a trough is only ever compared against a peak that preceded it.
const calculateMaxDDFromRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.performance_updated_at).getTime() -
      new Date(b.performance_updated_at).getTime()
  );
  let runningPeakEquity = null;
  let worstDD = 0;
  for (const row of sorted) {
    const value = Number(row.performance);
    if (Number.isNaN(value)) continue;
    const equity = 1 + value / 100; // return % -> equity index
    if (equity <= 0) continue; // guard against impossible total-loss inputs
    if (runningPeakEquity === null || equity > runningPeakEquity) {
      runningPeakEquity = equity;
    }
    const dd = ((equity - runningPeakEquity) / runningPeakEquity) * 100;
    if (dd < worstDD) worstDD = dd;
  }
  return runningPeakEquity === null ? null : worstDD;
};

// Mirror of NewPortfolioSection.calculateWinRate — counts profitable closed groups
// (total buy qty === total sell qty) over all closed groups.
const calculateWinRateFromRows = (rows) => {
  const groups = (rows || []).reduce((acc, t) => {
    if (!acc[t.group_id]) acc[t.group_id] = [];
    acc[t.group_id].push(t);
    return acc;
  }, {});
  let closedGroups = 0;
  let profitableGroups = 0;
  Object.entries(groups)
    .filter(
      ([groupId]) => groupId && groupId !== "null" && groupId !== "undefined"
    )
    .forEach(([, trades]) => {
      const totalBuyQty = trades
        .filter((t) => t.order_type === 1 || t.order_type === 5)
        .reduce((sum, t) => sum + parseFloat(t.order_quantity), 0);
      const totalSellQty = trades
        .filter((t) => t.order_type === 2 || t.order_type === 3)
        .reduce((sum, t) => sum + parseFloat(t.order_quantity), 0);
      if (totalBuyQty === totalSellQty) {
        closedGroups++;
        if (calculateGroupStatus(trades) === "profit") profitableGroups++;
      }
    });
  // No closed trades → win rate is undefined, not 0%. Return null so the podium
  // renders "—" (matches the profile card / avg hold time). A real all-losses
  // trader has closedGroups > 0 and still returns 0.
  if (closedGroups === 0) return null;
  return (profitableGroups / closedGroups) * 100;
};

const Avatar = ({ src, alt, sizeClass = "w-12 h-12" }) =>
  src ? (
    <img
      src={src}
      alt={alt}
      className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
    />
  ) : (
    <div
      className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0`}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-1/2 h-1/2">
        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
      </svg>
    </div>
  );

const FollowButton = ({ user, busy, onClick, size = "sm" }) => {
  if (!user) return null;
  const following = !!user.is_follow_by_me;
  const base =
    size === "lg"
      ? "w-full py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
      : "sm:px-4 px-2 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors disabled:opacity-60";
  const variant = following
    ? "bg-primaryColor text-white border border-primaryColor hover:opacity-90"
    : "bg-white text-primaryColor border border-primaryColor hover:bg-primaryColor/5";
  return (
    <button
      type="button"
      className={`${base} ${variant}`}
      onClick={(e) => onClick(e, user)}
      disabled={busy}
    >
      {following ? "Following" : "Follow"}
    </button>
  );
};

const PodiumCard = ({ rank, user, onNavigate, winRate, maxDD, isFeatured }) => {
  if (!user) return <div className="invisible" aria-hidden />;

  const { text: perfText, colorClass: perfColor } = formatPerformance(user);
  const medalColor = MEDAL_COLORS[rank];
  const isWinner = rank === 1;
  // Default "featured" → the winner (#1). On the mobile wheel the caller
  // overrides this with `isCenter` so the active card always gets the solid CTA.
  const featured = isFeatured ?? isWinner;

  return (
    <div className={`relative ${isWinner ? "" : "sm:mt-5"}`}>
      {isWinner && (
        <img
          src={`${process.env.PUBLIC_URL}/leadboardWinner.png`}
          alt="winner"
          className="absolute -top-6 left-1/2 -translate-x-1/2 w-10 sm:w-12 select-none pointer-events-none z-20"
        />
      )}
      <div
        className={`relative z-0 bg-white rounded-2xl px-2 py-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow flex flex-col border ${
          isWinner ? "shadow-none py-5" : "shadow-sm"
        }`}
        style={{ borderColor: medalColor }}
        onClick={(e) => onNavigate(e, user.id ?? user.user_id)}
      >
        {/* Badge (inline) + avatar + name + perf */}
        <div className="flex sm:items-start items-center gap-2 sm:gap-3 sm:flex-row flex-col">
          <span
            className="sm:w-8 w-7 sm:h-8 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-sm flex-shrink-0 sm:relative absolute sm:-left-2 -left-1 sm:-top-2 -top-1"
            style={{ backgroundColor: medalColor }}
          >
            {rank}
          </span>
          <Avatar
            src={user.image}
            alt={user.user_name}
            sizeClass="w-14 h-14 sm:w-16 sm:h-16"
          />
          <div className="flex-1 min-w-0">
            {/* Handle + tick + flag are one flex unit. justify-center (mobile)
                centers the WHOLE unit together so the flag never hangs off a
                left-shifted handle; the truncating handle (min-w-0) + shrink-0
                flag keep it on one line (no two-line wrap). */}
            <div className="flex items-center sm:justify-start justify-center gap-1 text-sm sm:text-base font-semibold text-primaryColor">
              <span className="truncate min-w-0">{user.user_name}</span>
              {user.green_tick && (
                <img
                  className="w-4 h-4 object-cover flex-shrink-0"
                  src={GreenTick}
                  alt="verified"
                />
              )}
              {user.code && (
                <span className="flex-shrink-0 flex items-center">
                  {renderFlag(user.code)}
                </span>
              )}
              <PrizeEligibleCheck user={user} />
            </div>
            <p
              className={`mt-0.5 text-xl sm:text-2xl font-extrabold leading-tight text-center sm:text-start ${perfColor}`}
            >
              {perfText}
            </p>
            <p className="text-[11px] sm:text-xs text-gray-500 text-center sm:text-start">Return</p>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-4 mb-3 border-t border-gray-200" />

        {/* Stat row. Non-featured (mobile #2/#3 side cards) get real column
            gutters and a hair-smaller font so Fans no longer collides with the
            Max DD column; the featured (#1) card is untouched. Dividers stay. */}
        <div
          className={`grid grid-cols-3 text-center sm:text-xs ${
            featured ? "text-[10px]" : "text-[9px]"
          }`}
        >
          <div className="flex flex-col px-1">
            <span
              className={`font-semibold text-gray-800 sm:text-sm ${
                featured ? "text-xs" : "text-[11px]"
              }`}
            >
              {winRate != null ? `${Math.round(Number(winRate))}%` : "—"}
            </span>
            <span className="text-gray-500 mt-0.5 leading-tight">
              <span className="sm:hidden">W%</span>
              <span className="hidden sm:inline">Win rate</span>
            </span>
          </div>
          <div className="flex flex-col px-1 border-l border-r border-gray-200">
            <span
              className={`font-semibold text-gray-800 sm:text-sm ${
                featured ? "text-xs" : "text-[11px]"
              }`}
            >
              {/* #1 keeps 2 decimals everywhere. On the narrow mobile #2/#3 side
                  cards, drop Max DD to a whole number so it can't collide; desktop
                  #2/#3 keep 2 decimals. */}
              {maxDD == null ? (
                "—"
              ) : featured ? (
                `${Math.abs(maxDD).toFixed(2)}%`
              ) : (
                <>
                  <span className="sm:hidden">{`${Math.abs(maxDD).toFixed(0)}%`}</span>
                  <span className="hidden sm:inline">{`${Math.abs(maxDD).toFixed(2)}%`}</span>
                </>
              )}
            </span>
            <span className="text-gray-500 mt-0.5 leading-tight">
              <span className="sm:hidden">DD</span>
              <span className="hidden sm:inline">Max DD</span>
            </span>
          </div>
          <div className="flex flex-col px-1">
            <span
              className={`font-semibold text-gray-800 sm:text-sm ${
                featured ? "text-xs" : "text-[11px]"
              }`}
            >
              {formatFollowers(user.follower_count)}
            </span>
            <span className="text-gray-500 mt-0.5 leading-tight">
              <span className="sm:hidden">{fansLabel(user.follower_count)}</span>
              <span className="hidden sm:inline">{fansLabel(user.follower_count)}</span>
            </span>
          </div>
        </div>

        {/* View Record button — links to the user's public profile/dashboard.
            Featured (centered / winner) = solid teal w/ white text.
            Others = outlined teal on white. */}
        <div className="mt-4" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => onNavigate(e, user.id ?? user.user_id)}
            className={`w-full py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
              featured
                ? "text-sm bg-primaryColor text-white border border-primaryColor hover:opacity-90"
                : "text-[10px] sm:text-sm bg-white text-primaryColor border border-primaryColor hover:bg-primaryColor/5"
            }`}
          >
            View Record
          </button>
        </div>
      </div>
    </div>
  );
};

// Three pre-recoloured PNG variants — only the gold accent band differs.
// The white body of the podium stays untouched, so we avoid the global tint
// the CSS `filter` approach used to introduce on rank 3.
const PODIUM_IMG = {
  1: "podium-stage-gold.png",
  2: "podium-stage-silver.png",
  3: "podium-stage-bronze.png",
};

const MobilePodiumWheel = ({
  podium,
  activeSlide,
  setActiveSlide,
  navigateToUserProfile,
  winRates,
  maxDDs,
}) => {
  // Order the wheel so it reads 2-1-3 left→right like the desktop podium (and the
  // standard silver-gold-bronze layout). In a 3-item circular wheel centered on
  // rank 1, index 1 sits on the RIGHT and index 2 wraps to the LEFT — so pushing
  // rank 3 before rank 2 lands #2 on the left and #3 on the right.
  const slides = [];
  if (podium[0]) slides.push({ rank: 1, user: podium[0] });
  if (podium[2]) slides.push({ rank: 3, user: podium[2] });
  if (podium[1]) slides.push({ rank: 2, user: podium[1] });
  const len = slides.length;
  const safeIdx = Math.min(activeSlide, len - 1);

  const goPrev = () => setActiveSlide((i) => (i - 1 + len) % len);
  const goNext = () => setActiveSlide((i) => (i + 1) % len);

  // Podium accent colour follows the centered card's rank.
  const activeRank = slides[safeIdx]?.rank ?? 1;

  // Swipe gesture — no library, just two refs and threshold logic.
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || len < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return; // ignore micro-drags
    if (dx < 0) goNext();
    else goPrev();
  };

  // Per-position transform/opacity/zIndex.
  // Center card stays flat (ry=0) and on top (z=30). Side cards tilt slightly
  // inward (±12°) and sit close to the center; the center's higher z-index
  // covers the small overlap so the outer content of each side card stays
  // fully readable.
  const layoutFor = (offset) => {
    if (offset === 0) return { x: 0, ry: 0, s: 1, o: 1, z: 30 };
    if (offset === -1) return { x: -118, ry: 34, s: 0.90, o: 1, z: 10 };
    if (offset === 1) return { x: 118, ry: -34, s: 0.90, o: 1, z: 10 };
    return { x: 0, ry: 0, s: 0, o: 0, z: 0 };
  };

  return (
    <div className="relative mt-14 mb-4">
      {/* Card carousel — items-end so cards sit at the bottom, just above the podium */}
      <div
        className="relative h-[290px] sm:h-[300px] flex items-end justify-center touch-pan-y pb-4"
        style={{ perspective: "1200px" }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {slides.map((slide, i) => {
          // Normalize offset to {-1, 0, 1} for a circular wheel.
          let offset = i - safeIdx;
          if (offset > len / 2) offset -= len;
          if (offset < -len / 2) offset += len;
          const t = layoutFor(offset);
          const isCenter = offset === 0;
          return (
            <div
              key={slide.rank}
              className={`absolute bottom-4 left-1/2 transition-all duration-500 ease-out cursor-pointer ${
                isCenter
                  ? "w-[42vw] min-w-[150px] max-w-[210px]"
                  : "w-[28vw] min-w-[100px] max-w-[155px]"
              }`}
              style={{
                transform: `translateX(-50%) translateX(${t.x}%) rotateY(${t.ry}deg) scale(${t.s})`,
                opacity: t.o,
                zIndex: t.z,
                transformStyle: "preserve-3d",
                pointerEvents: t.o === 0 ? "none" : "auto",
              }}
              onClick={
                isCenter
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      setActiveSlide(i);
                    }
              }
            >
              <PodiumCard
                rank={slide.rank}
                user={slide.user}
                onNavigate={navigateToUserProfile}
                winRate={winRates[slide.user?.id ?? slide.user?.user_id]}
                maxDD={maxDDs[slide.user?.id ?? slide.user?.user_id]}
                isFeatured={isCenter}
              />
            </div>
          );
        })}
      </div>
      {/* Podium base — rank-specific PNG (gold/silver/bronze). Only the gold
          accent band differs; the white body stays clean. */}
      <div className="relative -mt-[15px] w-[100%] mx-auto max-w-[500px]">
        <img
          aria-hidden
          src={`${process.env.PUBLIC_URL}/${PODIUM_IMG[activeRank] || PODIUM_IMG[1]}`}
          alt=""
          className="w-full select-none pointer-events-none transition-opacity duration-300"
        />
        {/* Soft radial shadow beneath the podium */}
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-[80%] h-3 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, rgba(0,0,0,0.10) 0%, transparent 70%)",
          }}
        />
      </div>
    </div>
  );
};

const LeaderboardRow = ({ user, rank, currentUserId, followBusy, onFollowToggle, onNavigate }) => {
  const { text: perfText, colorClass: perfColor } = formatPerformance(user);
  const isSelf = String(user.id ?? user.user_id ?? "") === String(currentUserId ?? "");
  const handleRowClick = (e) => onNavigate(e, user.id ?? user.user_id);
  const stopClick = (e) => e.stopPropagation();
  const chevron = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-4 h-4 text-gray-400 flex-shrink-0"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
  // Shown in place of the Follow button when the row is the logged-in user.
  const youPill = (
    <span className="text-gray-400 text-sm font-semibold select-none">—</span>
  );
  const rowHighlight = isSelf ? "bg-veryLightGreen/40" : "";
  return (
    <div
      className={`cursor-pointer ${rowHighlight}`}
      onClick={handleRowClick}
    >
      {/* Mobile layout (<sm = 640px): single compact row —
          rank · avatar · @handle · perf% · chevron. No follow / dash here. */}
      <div className="sm:hidden flex items-center gap-2 py-3 px-3 hover:bg-gray-50">
        <span className="text-primaryColor font-bold text-sm flex-shrink-0 w-5 text-center">
          {rank}
        </span>
        <Avatar src={user.image} alt={user.user_name} sizeClass="w-8 h-8" />
        <p className="flex-1 min-w-0 flex items-center gap-1 text-sm text-primaryColor font-semibold">
          <span className="truncate">{user.user_name}</span>
          {user.green_tick && (
            <img
              className="w-4 h-4 object-cover flex-shrink-0"
              src={GreenTick}
              alt="verified"
            />
          )}
          {renderFlag(user.code)}
          <PrizeEligibleCheck user={user} />
        </p>
        <p className={`flex-shrink-0 font-bold text-sm ${perfColor}`}>
          {perfText}
        </p>
        {chevron}
      </div>

      {/* Tablet & desktop layout (>=sm): equal-share columns so perf,
          followers and the follow button spread across the row width */}
      <div className="hidden sm:flex items-center gap-3 sm:gap-6 py-3 sm:py-4 hover:bg-gray-50">
        <span className="w-6 sm:w-8 text-primaryColor font-bold text-base sm:text-lg flex-shrink-0 text-center">
          {rank}
        </span>
        <Avatar
          src={user.image}
          alt={user.user_name}
          sizeClass="w-10 h-10 sm:w-11 sm:h-11"
        />
        <div className="flex-1 min-w-0">
          <p className="flex items-center gap-1 text-sm sm:text-base text-primaryColor font-semibold">
            <span className="truncate">{user.user_name}</span>
            {user.green_tick && (
              <img
                className="w-4 h-4 object-cover flex-shrink-0"
                src={GreenTick}
                alt="verified"
              />
            )}
            {renderFlag(user.code)}
            <PrizeEligibleCheck user={user} />
          </p>
        </div>
        <p className={`flex-1 min-w-[60px] font-bold text-sm sm:text-base ${perfColor}`}>
          {perfText}
        </p>
        <p className="flex-1 min-w-[100px] text-gray-700 text-xs sm:text-sm flex items-baseline gap-1 leading-tight">
          <span className="font-semibold text-gray-900">{formatFollowers(user.follower_count)}</span>
          <span className="text-gray-500">{fansLabel(user.follower_count)}</span>
        </p>
        {/* Always reserve this column so the row layout stays consistent
            whether the user can be followed or is the logged-in user. */}
        <div className="flex-shrink-0 w-[140px] flex justify-center" onClick={stopClick}>
          {isSelf ? (
            youPill
          ) : (
            <FollowButton
              user={user}
              busy={followBusy.has(user.uuid)}
              onClick={onFollowToggle}
              size="lg"
            />
          )}
        </div>
        {chevron}
      </div>
    </div>
  );
};

const LeaderboardSecton = (props) => {
  const game_id = props.gameId ?? null;
  const slug = props.slug ?? null;
  // The league itself, when the caller has it. Only the STANDALONE leaderboard
  // passes it, and it is what lets this one component carry both of the states
  // the client asked for ("Reuse the existing leaderboard component with live and
  // completed states. Do not create duplicated leaderboard components.") — see
  // `isCompletedLeague` and the personal message below.
  const game = props.game ?? null;
  // True on /leaderboards/<slug>: the leaderboard opened on its own, outside the
  // league shell, from a completed card or a live league's View Leaderboard CTA.
  const standalone = !!props.standalone;
  // True when the revamped league shell is up — i.e. the user is a member and the
  // left rail has already become this league's nav.
  const inLeagueShell = useLeagueContext().isMember;
  // A finished league's board is a RECORD, so the copy around it changes: "You
  // finished #2" rather than "You're ranked #2". The figures themselves are
  // already frozen by the backend (UserController::finalisedGamesQuery), so this
  // is wording only.
  // Client: "Cash Game leaderboard screen and the preview leaderboard screen both
  // say Verified league performance. needs to be change to game performance."
  //
  // Both screens are THIS component, which is why one line covers them: the
  // in-league tab and the standalone board differ only in what wraps them.
  // leagueTypeKey is League Play's own classifier and checks
  // is_cash_prize_league before public/private, so a cash game is never
  // mislabelled as a league here the way it would be off game_type alone.
  //
  // Outside a league the sitewide wording is untouched — that board is the main
  // QUSHi record, not any competition's.
  const verifiedLine = !game_id
    ? "Verified QUSHi performance"
    : leagueTypeKey(game) === "cash"
    ? "Verified game performance"
    : "Verified league performance";
  const isCompletedLeague =
    !!game &&
    (game.status === "completed" || getLeagueStatus(game)?.tone === "ended");

  const [leaderBoardData, setLeaderBoardData] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed board request replaces the skeleton with a section error instead of
  // leaving grey rows on screen indefinitely.
  const [loadFailed, setLoadFailed] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tab, setTab] = useState("lifetime");
  const [followBusy, setFollowBusy] = useState(() => new Set());
  const [scrollPosition, setScrollPosition] = useState(0);
  const [winRates, setWinRates] = useState({});
  const [maxDDs, setMaxDDs] = useState({});
  const [isMobileSlider, setIsMobileSlider] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 574px)").matches
      : false
  );
  const [activeSlide, setActiveSlide] = useState(0);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const currentUserId = localStorage.getItem("user_id");
  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: () => fetchForTab(),
    disabled: false,
  });

  const scrollPositionKey = "leaderBoradPageScroll";

  const initScrollPosition = () => {
    localStorage.setItem(
      scrollPositionKey,
      JSON.stringify({ scrollPosition, itemsPerPage })
    );
  };

  const clearScrollPosition = () => {
    const saved = localStorage.getItem(scrollPositionKey)
      ? JSON.parse(localStorage.getItem(scrollPositionKey))
      : null;
    if (saved) {
      setItemsPerPage(saved.itemsPerPage);
      setTimeout(() => {
        window.scrollTo(0, saved.scrollPosition);
      }, 100);
      localStorage.removeItem(scrollPositionKey);
    }
  };

  const fetchForTab = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      let list = [];
      if (tab === "lifetime") {
        const res = await getLeaderBoard(game_id);
        if (res?.data?.status === 200) {
          list = res?.data?.data?.data ?? [];
        }
      } else {
        const period =
          tab === "monthly" ? "month" : tab === "weekly" ? "week" : "day";
        const res = await getLeaderboardData(period);
        list = res?.data?.performers ?? [];
      }
      setLeaderBoardData(list);
      const topId = list?.[0]?.id ?? list?.[0]?.user_id;
      if (topId) localStorage.setItem("qushi_top_trader_id", String(topId));

      if (tab === "lifetime") {
        const toggle = localStorage.getItem("toggle") === "true";
        if (toggle) {
          localStorage.setItem("toggle", "false");
          clearScrollPosition();
        }
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setLeaderBoardData([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, game_id]);

  useEffect(() => {
    const handleScroll = () => setScrollPosition(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Switch podium → slider layout when viewport drops below 575px.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(max-width: 574px)");
    const handler = (e) => setIsMobileSlider(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Reset slider position whenever the active leaderboard tab changes.
  useEffect(() => {
    setActiveSlide(0);
  }, [tab]);


  // Fetch PerformanceHistory rows for top-3 users in the current tab's period
  // and compute Max Drawdown for each.
  useEffect(() => {
    const top = leaderBoardData.slice(0, 3);
    if (top.length === 0) {
      setMaxDDs({});
      return undefined;
    }
    let cancelled = false;
    // Max DD is always LIFETIME — never scoped to the active TAB. The top-3 set
    // still changes per tab (so this effect re-runs), but the window is fixed at
    // epoch→now; the backend clamps the start to user.last_reset.
    //
    // Scoped to the LEAGUE, though, and that is a different axis. Client Rev 4:
    // "I still see my main record's max drawdown percentage inside a league
    // leaderboard podium card. These stats need to stay separate. I believe the
    // user's league profile stats may be associated only with the league stats,
    // because I do not see my 9.98% max drawdown there. I only see the number in
    // the leaderboard podium card stat."
    //
    // Exactly right, and this line is why. The podium does not read max_drawdown
    // off the leaderboard row — the backend scopes that one correctly already
    // (LeaderBoardController passes $boardGameId to Helper::maxDrawdown). It
    // computes its own from performance history, and it was asking for the
    // SITEWIDE history: game_id was in this effect's dependency list but never in
    // the request. So one card could show a league Return beside a whole-of-QUSHi
    // drawdown, while the league profile two taps away showed the league's — the
    // disagreement the client is reporting. The win rate beside it has been
    // passing game_id all along (see the next effect).
    const startDate = new Date(0);
    const endDate = new Date();
    const ids = top.map((u) => u.id ?? u.user_id).filter(Boolean);
    Promise.all(
      ids.map(async (uid) => {
        try {
          const res = await getUserPerformance(uid, startDate, endDate, game_id);
          const rows = Array.isArray(res?.data) ? res.data : [];
          return [uid, calculateMaxDDFromRows(rows)];
        } catch (err) {
          console.error("max DD fetch failed for", uid, err);
          return [uid, null];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      for (const [uid, v] of results) map[uid] = v;
      setMaxDDs(map);
    });
    return () => {
      cancelled = true;
    };
  }, [leaderBoardData, game_id, tab]);

  // Fetch closed trades for top-3 users and compute their win rate
  // (mirrors the calculation on the Portfolio → Closed Trades page).
  useEffect(() => {
    const top = leaderBoardData.slice(0, 3);
    if (top.length === 0) {
      setWinRates({});
      return undefined;
    }
    let cancelled = false;
    const ids = top.map((u) => u.id ?? u.user_id).filter(Boolean);
    Promise.all(
      ids.map(async (uid) => {
        try {
          const res = await getOrder(2, uid, game_id);
          const rows = res?.data?.data?.data || [];
          return [uid, calculateWinRateFromRows(rows)];
        } catch (err) {
          console.error("win rate fetch failed for", uid, err);
          return [uid, null];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      for (const [uid, wr] of results) map[uid] = wr;
      setWinRates(map);
    });
    return () => {
      cancelled = true;
    };
  }, [leaderBoardData, game_id]);

  const handleLoadMore = () => setItemsPerPage((n) => n + 10);

  const navigateToUserProfile = (event, userId) => {
    if (event) event.preventDefault();
    // Reported as "leaderboard par username/avatar click pe kuch nahi hota".
    // It was not the avatar: the whole podium card AND the View Record button
    // went through here, and this line used to read
    // `if (private_portfolio || !userId) return;` — so in ANY league whose
    // portfolio visibility is "Hidden during league" every member card on the
    // leaderboard was silently dead, including your own.
    //
    // That blanket block is exactly what the client's League Play Profiles
    // item 21 replaces: the profile is supposed to OPEN and show Posts,
    // Performance and the League Record in full, with only Holdings and Trade
    // History locked (Profile.jsx `portfolioLocked`). Blocking the whole
    // profile hid four things to protect two.
    if (!userId) return;
    initScrollPosition();
    window.scrollTo(0, 0);
    // Client, "League Profile Routing": "When accessed from a standalone
    // leaderboard, the league stats profile must also remain outside the league
    // shell." /<slug>/profile/<id> IS inside the shell (it is in
    // LEAGUE_SUB_ROUTES), so a standalone board hands out the standalone
    // address instead. Same screen, same component — see LeagueProfileStandalone.
    if (game_id && standalone) {
      navigate(standaloneLeagueProfilePath(slug, userId), {
        state: { game_id, slug },
      });
    } else if (game_id) {
      navigate(`/${slug}/profile/${userId}`, { state: { game_id, slug } });
    } else {
      navigate(`/profile/${userId}`, { state: { game_id, slug } });
    }
  };

  const handleFollowToggle = async (e, user) => {
    if (e) e.stopPropagation();
    const uuid = user?.uuid;
    if (!uuid) return;
    if (followBusy.has(uuid)) return;

    const was = !!user.is_follow_by_me;
    const nextStatus = was ? 1 : 2;

    setLeaderBoardData((prev) =>
      prev.map((u) => (u.uuid === uuid ? { ...u, is_follow_by_me: !was } : u))
    );
    setFollowBusy((prev) => {
      const next = new Set(prev);
      next.add(uuid);
      return next;
    });

    try {
      const res = await followUnfollow({ user_uuid: uuid, request_status: nextStatus });
      const failed = res?.status !== 200 || res?.data?.status === 400;
      if (failed) {
        setLeaderBoardData((prev) =>
          prev.map((u) => (u.uuid === uuid ? { ...u, is_follow_by_me: was } : u))
        );
      }
    } catch (err) {
      setLeaderBoardData((prev) =>
        prev.map((u) => (u.uuid === uuid ? { ...u, is_follow_by_me: was } : u))
      );
      console.error("follow toggle failed", err);
    } finally {
      setFollowBusy((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  const filteredData = searchQuery
    ? leaderBoardData.filter((u) =>
        u.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : leaderBoardData;

  const podium = filteredData.slice(0, 3);
  const rest = filteredData.slice(3);

  let currentUserRank = null;
  let currentUserRow = null;
  if (currentUserId) {
    const idx = leaderBoardData.findIndex(
      (u) =>
        String(u.id ?? "") === String(currentUserId) ||
        String(u.user_id ?? "") === String(currentUserId)
    );
    if (idx >= 0) {
      currentUserRank = idx + 1;
      currentUserRow = leaderBoardData[idx];
    }
  }
  // The viewer's own final number, formatted by the same function that paints
  // every other figure on this board — client: "use same color rules for
  // return". So a negative final return can never be printed green here, and a
  // flat one stays grey with no sign.
  const currentUserReturn = currentUserRow
    ? formatPerformance(currentUserRow)
    : null;

  // Switching period swaps the dataset wholesale, so a skeleton is correct on a
  // tab change as well as on first load (the documented exception to "a rendered
  // screen never goes backwards").
  const showBoardSkeleton = useDelayedFlag(loading && !loadFailed);

  // Client Rev 3, mobile inside a private league, Leaderboard: "the league
  // leaderboard screen doesn't seem to use the full width like the main
  // leaderboard screen" — and, desktop: "We want our desktop league leaderboards
  // to match the main site's desktop leaderboard in terms of container
  // width/size."
  //
  // Both are the same fault. Standing alone at /leaderboard this component IS the
  // page, so it brings its own page furniture: PageContainer (the sidebar offset,
  // the px-2 gutter and the white shadow panel) plus its own px-4/sm:px-6/lg:px-8
  // and a pt-20/md:pt-24 that clears the fixed header. Inside the league shell all
  // of that is already supplied ONE level up, so every piece of it was applied a
  // second time.
  //
  // Measured at 390px BEFORE: shell content ran from x=16 to x=374 while the board
  // sat at x=40 with 310px of width — 48px narrower than the screen it lives on.
  // At 1440px the board was a 976px white card floating inside a 1232px shell.
  // AFTER: the board inherits the shell's own container, so it starts on the same
  // left edge as the league title and runs the shell's full width.
  //
  // Outside a league nothing changes — Shell is still PageContainer with the same
  // props, and the class string is character-for-character the original.
  //
  // `standalone` is the third case. The board is a page of the MAIN site there,
  // so it wants PageContainer's ordinary single-column surface — not the
  // two-column `game_page_with_sidebar` grid, which exists purely to make room
  // for the legacy GameSideBar that a standalone board does not render.
  const Shell = inLeagueShell ? React.Fragment : PageContainer;
  const shellProps = inLeagueShell
    ? {}
    : { gamePage: game_id && !inLeagueShell && !standalone ? true : false };
  const boardContainerClass = inLeagueShell
    ? // No gutters (the shell owns them) and a small, deliberate gap under the
      // identity divider rather than a header-clearing pt-20.
      "pt-6 pb-24"
    : "max-w-[72rem] lg:max-w-none mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-24 pb-24";

  return (
    // Inside a league the left rail IS the league's nav, so the legacy in-page
    // league menu (Posts / Buy-Sell / Charts / Portfolio / Leaderboard + Exit
    // League) would be a second, older copy of it sitting beside the board. Drop
    // it, and the two-column grid with it: a member gets the same leaderboard the
    // rest of the site uses, scoped to this league.
    <Shell {...shellProps}>
      {game_id && !inLeagueShell && !standalone && (
        <GameSideBar activeMenu="leaderboard" slug={slug} />
      )}
      <div className={boardContainerClass} {...containerProps}>
        <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
        {/* The standalone board's own chrome — back arrow, league identity and
            the Live now / Completed status line — rendered HERE rather than by
            the page above, so it shares this container's gutters and lands on the
            exact left edge the podium and the rows do. A page-level header would
            sit outside PageContainer and be 16-32px out at every breakpoint.
            Nothing renders it in the other two modes. */}
        {props.header || null}
        {/* Header: title + subtitle on left, tabs on right */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 md:mb-8">
          <div>
            {/* House rule: flagship page title — title-page token (size),
                Leaderboard font/weight, #004d4d. */}
            <h1 className="text-title-page font-bold text-[#004d4d]">
              Leaderboard
            </h1>
            <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
              <svg
                viewBox="0 0 24 24"
                fill="#16A34A"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 flex-shrink-0"
                aria-hidden="true"
              >
                <path d="M12 1.5l2.39 2.05 3.13-.39.74 3.07 2.74 1.6-1.35 2.85 1.35 2.85-2.74 1.6-.74 3.07-3.13-.39L12 19.86l-2.39-2.05-3.13.39-.74-3.07-2.74-1.6 1.35-2.85L3 7.83l2.74-1.6.74-3.07 3.13.39L12 1.5z" />
                <path d="M10.6 14.6l-2.7-2.7 1.4-1.4 1.3 1.3 4.1-4.1 1.4 1.4-5.5 5.5z" fill="#fff" />
              </svg>
              {/* Client Rev 2, Leaderboard 6: "Change the supporting line Verified
                  QUSHi performance to Verified league performance." Inside a
                  league the figures ARE the league's, so the sitewide wording
                  would be claiming the wrong thing; outside one it is still the
                  main record and keeps its own wording. */}
              <span>{verifiedLine}</span>
            </div>
          </div>
          {/* Client Rev 2, Leaderboard 3 & 4: "Remove the Lifetime / Monthly /
              Weekly / Daily filters. League leaderboard ranking is cumulative for
              the entire league period, from league start through the current time
              or league end. So I guess we leave it on the Lifetime setting."
              Hidden rather than removed: `tab` still drives the fetch, and it is
              seeded to "lifetime", which IS cumulative — so a league leaderboard
              asks the API for exactly the range the client described while the
              main QUSHi leaderboard keeps all four filters. */}
          <div className={`w-full gap-2 md:w-auto ${game_id ? "hidden" : "flex"}`}>
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex-1 md:flex-none px-3 sm:px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-60 ${
                    active
                      ? "bg-primaryColor text-white border border-primaryColor"
                      : "bg-white text-primaryColor border border-primaryColor/30 hover:border-primaryColor"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {loadFailed ? (
          <SectionError
            message="Leaderboard data could not be loaded"
            onRetry={fetchForTab}
          />
        ) : showBoardSkeleton ? (
          // Podium + rows skeleton. Title, subtitle and the period tabs above
          // are already on screen and stay interactive.
          <LeaderboardSkeleton />
        ) : loading ? null : filteredData.length === 0 ? (
          <div className="text-center text-gray-500 text-lg mt-10">
            {/* Client, leaderboard preview: "Empty-state copy: replace 'No one is
                in the leaderboard now' with something that reads as early, not
                dead. Replace with: 'Standings appear once traders make their
                first move.'"

                Their sentence verbatim. The old line described the board as
                empty; this one describes it as early, which on a game that has
                not opened yet is the true reading. A no-results search is a
                different fact and keeps its own message. */}
            {searchQuery
              ? `No users found for "${searchQuery}".`
              : "Standings appear once traders make their first move."}
          </div>
        ) : (
          <>
            {/* Top-3 podium */}
            {podium.length > 0 && !isMobileSlider && (
              <div className="mb-8 mt-4 pt-8">
                <div className="grid grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
                  <PodiumCard
                    rank={2}
                    user={podium[1]}
                    onNavigate={navigateToUserProfile}
                    winRate={winRates[podium[1]?.id ?? podium[1]?.user_id]}
                    maxDD={maxDDs[podium[1]?.id ?? podium[1]?.user_id]}
                  />
                  <PodiumCard
                    rank={1}
                    user={podium[0]}
                    onNavigate={navigateToUserProfile}
                    winRate={winRates[podium[0]?.id ?? podium[0]?.user_id]}
                    maxDD={maxDDs[podium[0]?.id ?? podium[0]?.user_id]}
                  />
                  <PodiumCard
                    rank={3}
                    user={podium[2]}
                    onNavigate={navigateToUserProfile}
                    winRate={winRates[podium[2]?.id ?? podium[2]?.user_id]}
                    maxDD={maxDDs[podium[2]?.id ?? podium[2]?.user_id]}
                  />
                </div>
              </div>
            )}

            {/* Mobile podium — 3D coverflow wheel (<575px). Center card showcased;
                #2 and #3 tilt to the sides. Swipe / tap side card / arrows / dots all rotate. */}
            {podium.length > 0 && isMobileSlider && (
              <MobilePodiumWheel
                podium={podium}
                activeSlide={activeSlide}
                setActiveSlide={setActiveSlide}
                navigateToUserProfile={navigateToUserProfile}
                winRates={winRates}
                maxDDs={maxDDs}
              />
            )}

            {/* You're ranked card — always shown to a logged-in viewer. New
                signups aren't on the board yet, so instead of hiding the card we
                show a dormant empty state: same icon, but the circle outline and
                arrow switch from green (active) to secondary grey (dormant); the
                white fill stays. */}
            {currentUserId && (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 sm:px-5 py-2 mb-3 flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-full bg-white flex items-center justify-center border flex-shrink-0 ${
                    currentUserRank ? "border-greenXD/30" : "border-secondary"
                  }`}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`w-5 h-5 ${
                      currentUserRank ? "text-greenXD" : "text-secondary"
                    }`}
                  >
                    <polyline points="3 17 9 11 13 15 21 7" />
                    <polyline points="14 7 21 7 21 14" />
                  </svg>
                </div>
                <div>
                  {/* Client, "Leaderboard Status":
                        Live personal message:      You're ranked #2. Keep
                                                    trading and proving it.
                        Completed personal message: You finished #2 and Final
                                                    return 0.00%

                      One card, two states, rather than a second card for
                      completed leagues — "You're ranked #2" is a claim about a
                      board that is still moving, and on a finished league it is
                      simply wrong. "Keep trading and proving it" is worse: the
                      league is over and there is nothing left to trade. */}
                  {currentUserRank && isCompletedLeague ? (
                    <>
                      <p className="font-semibold text-base sm:text-lg text-primaryColor">
                        You finished{" "}
                        <span className="text-[#D7C588] font-bold">#{currentUserRank}</span>
                      </p>
                      {currentUserReturn && (
                        <p className="text-sm text-gray-500">
                          Final return{" "}
                          <span className={`font-semibold ${currentUserReturn.colorClass}`}>
                            {currentUserReturn.text}
                          </span>
                        </p>
                      )}
                      <PrizeProgressLine eligibility={currentUserRow?.prize_eligibility} />
                    </>
                  ) : currentUserRank ? (
                    <>
                      <p className="font-semibold text-base sm:text-lg text-primaryColor">
                        You're ranked{" "}
                        <span className="text-[#D7C588] font-bold">#{currentUserRank}</span>
                      </p>
                      <p className="text-sm text-gray-500">Keep trading and proving it.</p>
                      <PrizeProgressLine eligibility={currentUserRow?.prize_eligibility} />
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-base sm:text-lg text-primaryColor">
                        Nobody starts ranked
                      </p>
                      <p className="text-sm text-gray-500">
                        The board only knows real positions. One trade puts you on it.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* League Play info banner */}
            {/* <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 sm:px-5 py-3 mb-6 flex items-center gap-3">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-gray-400 flex-shrink-0"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <p className="text-sm text-gray-600">
                League Play results are separate from the main leaderboard.
              </p>
            </div> */}

            {/* Ranked list (positions 4+) */}
            {rest.length > 0 && (
              <div className="bg-white divide-y divide-gray-200">
                {rest.slice(0, itemsPerPage).map((user, i) => (
                  <LeaderboardRow
                    key={user.id ?? user.user_id ?? i}
                    user={user}
                    rank={i + 4}
                    currentUserId={currentUserId}
                    followBusy={followBusy}
                    onFollowToggle={handleFollowToggle}
                    onNavigate={navigateToUserProfile}
                  />
                ))}
              </div>
            )}

            {rest.length > itemsPerPage && (
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="py-2 px-4 text-base font-medium text-gray-500"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
};

export default LeaderboardSecton;
